import { Course } from "@api";
import { Alert, Button, Divider, List, ListItem, ListItemButton, ListItemText, Stack, TextField, Typography } from "@mui/material"
import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom";
import { parseError } from "sprungkraft/helper/helper_functions";
import { useStore } from "sprungkraft/state/state";
import AdminPage from "sprungkraft/widgets/admin_page";
import LoadingWidget from "sprungkraft/widgets/loading_widget";

const COURSES_INFO_TEXT_KEY = 'courses_info_text';

const AdminCourseList = () => {
    const [courses, setCourses] = useState<Course[] | null>(null);
    const [infoText, setInfoText] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [infoError, setInfoError] = useState<string | null>(null);
    const [infoSaved, setInfoSaved] = useState(false);
    const navigate = useNavigate()
    const {api: {courseApi, settingApi}} = useStore();

    const goToCourse = (course: Course) => {
        navigate(`/admin/course/${course.uuid}`)
    }

    const goToAddCourse = () => {
        navigate('/admin/course-add')
    }

    useEffect(() => {
        const sub = courseApi.getCourses().subscribe(setCourses);
        return () => { sub.unsubscribe(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const sub = settingApi.getSetting({ key: COURSES_INFO_TEXT_KEY }).subscribe({
            next: (setting) => setInfoText(setting.value),
            error: (err) => setInfoError(parseError(err)),
        });
        return () => { sub.unsubscribe(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const saveInfoText = () => {
        if (infoText === null) return;
        setSaving(true);
        setInfoError(null);
        setInfoSaved(false);
        settingApi.changeSetting({ key: COURSES_INFO_TEXT_KEY, changeSetting: { value: infoText } }).subscribe({
            next: () => { setSaving(false); setInfoSaved(true); },
            error: (err) => { setSaving(false); setInfoError(parseError(err)); },
        });
    }

    const coursesListWidget = (courses: Course[]) => {
        if (courses.length === 0) {
            return [<Alert severity="info">Kein Kurs enthalten. Fügen Sie Ihren ersten Kurs hinzu.</Alert>]
        }
        return courses.map(course => (
            <ListItem key={course.uuid} >
                <ListItemButton onClick={() => goToCourse(course)}>
                    <ListItemText primary={course.title} />
                </ListItemButton>
            </ListItem>
        ))
    }

    return (<AdminPage>
        <Stack spacing={1} sx={{ mb: 3 }}>
            <Typography variant="h6">Info-Text auf der Anmeldeseite</Typography>
            <Typography variant="body2" color="text.secondary">
                Dieser Text wird auf der Anmeldeseite unter der Überschrift „Kurse“ angezeigt. Links (http://…) werden automatisch anklickbar.
            </Typography>
            {infoText === null && infoError === null
                ? <LoadingWidget />
                : <TextField
                    multiline
                    minRows={3}
                    value={infoText ?? ''}
                    onChange={(e) => { setInfoText(e.target.value); setInfoSaved(false); }}
                    fullWidth
                />}
            {infoError && <Alert severity="error">{infoError}</Alert>}
            {infoSaved && <Alert severity="success">Gespeichert</Alert>}
            {saving && <LoadingWidget />}
            <Button variant="contained" onClick={saveInfoText} disabled={saving || infoText === null}>Speichern</Button>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="h6"> Kurse </Typography>
        <List>
            {courses ? coursesListWidget(courses) : <LoadingWidget />}
        </List>
        <Button variant="outlined" onClick={goToAddCourse}>Kurs hinzufügen</Button>
    </AdminPage>)
}

export default AdminCourseList